'use client'

import { useMemo, useState } from 'react'

import { Check, ChevronsUpDown } from 'lucide-react'
import { useController } from 'react-hook-form'

import { cn } from '@/shared/lib/utils'
import { useFormField } from '@/shared/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

import type { AsyncConfig, FieldValueSingle, SelectOption } from '../Select/types'
import { useSelectOptions } from '../Select/useSelectOptions'
import { toStr, useDebouncedValue } from '../Select/utils'

export interface ComboboxProps {
  options?: SelectOption[]
  async?: AsyncConfig
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  defaultValue?: FieldValueSingle
  onChange?: (value: FieldValueSingle, record?: unknown) => void
  beforeChange?: (value: FieldValueSingle) => FieldValueSingle
}

export function Combobox({
  options: staticOptions,
  async,
  placeholder = 'Selecione...',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum resultado encontrado.',
  disabled,
  className,
  defaultValue,
  onChange,
  beforeChange
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const isAsync = !!async?.url
  const debounceMs = async?.debounceMs ?? 300
  const debouncedSearch = useDebouncedValue(search, debounceMs)

  const { name } = useFormField()
  const { field } = useController({
    name,
    defaultValue: defaultValue ?? null,
    disabled
  })

  const { options, loading, optionLookup } = useSelectOptions(
    staticOptions,
    async,
    isAsync ? debouncedSearch : ''
  )

  const visibleOptions = useMemo(() => {
    if (isAsync || !search.trim()) return options
    const term = search.trim().toLowerCase()
    return options.filter(option => option.label.toLowerCase().includes(term))
  }, [options, isAsync, search])

  const selected =
    field.value !== null && field.value !== undefined
      ? optionLookup.get(toStr(field.value))
      : undefined

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSearch('')
  }

  const handleSelect = (option: SelectOption) => {
    let nextValue: FieldValueSingle = option.value
    if (beforeChange) nextValue = beforeChange(nextValue)
    field.onChange(nextValue)
    onChange?.(nextValue, option.record)
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onBlur={field.onBlur}
          className={cn(
            className,
            'flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60'
          )}
        >
          <span className={cn('truncate', !selected && 'text-white/40')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] rounded-xl border-white/15 bg-brand-dark p-0 text-white shadow-xl"
        onOpenAutoFocus={event => event.preventDefault()}
      >
        <div className="border-b border-white/10 p-2">
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none placeholder:text-white/40 focus-visible:border-primary [color-scheme:dark]"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {loading ? (
            <div className="px-3 py-2 text-sm text-white/50">Carregando...</div>
          ) : visibleOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-white/50">{emptyMessage}</div>
          ) : (
            visibleOptions.map(option => (
              <button
                key={toStr(option.value)}
                type="button"
                onClick={() => handleSelect(option)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-white/10',
                  toStr(field.value) === toStr(option.value) && 'bg-white/10'
                )}
              >
                <span className="truncate">{option.label}</span>
                {toStr(field.value) === toStr(option.value) && (
                  <Check className="size-4 shrink-0 text-primary" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
